import { z } from 'zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';

import { useConnectWhatsApp } from './useConnectWhatsApp';

export function useWhatsappStep2() {
  const { t } = useTranslate('translation');
  const {
    state: channelState,
    handleChannelName,
    handleWhatsappPin,
    handleFaceBookDeteail,
    onChangeStep,
  } = useChannelContext();

  const schema = z.object({
    channelName: z.string().min(1, { message: t('CHANNEL_NAME_REQUIRED') }),
    pin: z
      .string()
      .min(6, { message: t('PIN_MUST_BE_6_DIGITS') })
      .max(6, { message: t('PIN_MUST_BE_6_DIGITS') })
      .regex(/^\d{6}$/, { message: t('PIN_NUMERIC_ONLY') }),
    pinAcknowledge: z.boolean().refine((val) => val === true, {
      message: t('PLEASE_ACKNOWLEDGE_WARNINGS'),
    }),
  });

  type FormValues = z.infer<typeof schema>;

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      channelName: '',
      pin: '',
      pinAcknowledge: false,
    },
    values: {
      channelName: channelState.channelName,
      pin: channelState.whatsappPin,
      pinAcknowledge: false,
    },
  });

  const { handleSubmit, watch, formState } = methods;

  const channelName = watch('channelName');
  const isFormValid = formState.isValid;

  const { connect, isWhatsappConnectSuccess } = useConnectWhatsApp({
    onSuccess: handleFaceBookDeteail,
  });

  // Sync form state to context on change to ensure fresh state for navigation
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.channelName) handleChannelName(value.channelName);
      if (value.pin) handleWhatsappPin(value.pin);
    });
    return () => subscription.unsubscribe();
  }, [watch, handleChannelName, handleWhatsappPin]);

  const onSubmit = handleSubmit(async (data) => {
    handleChannelName(data.channelName);
    handleWhatsappPin(data.pin);
    methods.reset();
    await onChangeStep('next');
  });

  const isNextDisabled = !isFormValid || !isWhatsappConnectSuccess;

  return {
    methods,
    channelName,
    isWhatsappConnectSuccess,
    isNextDisabled,
    connect,
    onSubmit,
  };
}
